<template>
    <Toast />

    <div class="col-xs-12 col-sm-12 col-md-12 col-lg-12">
        <div :class="`card-header ${pageBox}`">
            <h4 class="m-b-0 text-white" style="display: inline">
                <i :class="$t('whitelist.icon')"></i>
                Entries
            </h4>
        </div>
    </div>

    <div class="col-xs-12 col-sm-12 col-md-12 col-lg-12">
        <Panel>
            <DataTable
                :value="data"
                dataKey="id"
                v-model:filters="filters"
                filterDisplay="row"
                :loading="loading"
                paginator
                :rows="10"
                :rowsPerPageOptions="[5, 10, 20, 50]"
                stripedRows
                removableSort
                :sortable="true"
                :totalRecords="totalRecords"
                paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                currentPageReportTemplate="{first} to {last} of {totalRecords}"
            >
                <!-- ─── Global Search ─── -->
                <template #header>
                    <div class="row justify-content-end pt-2">
                        <div>
                            <IconField>
                                <InputIcon>
                                    <i class="fa-solid fa-magnifying-glass"></i>
                                </InputIcon>
                                <InputText
                                    v-model="filters.global.value"
                                    placeholder="Search"
                                />
                            </IconField>
                        </div>
                    </div>
                </template>

                <!-- ─── No Data State ─── -->
                <template #empty>
                    <div class="d-flex justify-content-center align-items-center py-4">
                        <div class="text-center">
                            <h4 v-if="!loading">No data found.</h4>
                        </div>
                    </div>
                </template>

                <!-- ─── Loading State ─── -->
                <template #loading>
                    <div class="d-flex flex-column align-items-center justify-content-center py-4 text-dark">
                        <i class="fa-solid fa-spinner fa-spin fa-4x"></i>
                    </div>
                </template>

                <!-- ─── Dynamic Columns ─── -->
                <Column
                    v-for="col in columns"
                    :key="col.field"
                    :field="col.field"
                    :filterField="col.field"
                    :header="col.header"
                    :sortable="col.sortable"
                    :sortField="col.field"
                    :filter="col.filter"
                    :show-filter-menu="false"
                >
                    <!-- Sort Icons -->
                    <template #sorticon="{ sortOrder }">
                        <i v-if="sortOrder === 1" class="fa-solid fa-sort-up"></i>
                        <i v-else-if="sortOrder === -1" class="fa-solid fa-sort-down"></i>
                        <i v-else class="fa-solid fa-sort"></i>
                    </template>

                    <!-- Data Cell Rendering -->
                    <template #body="{ data: rowData }">
                        <span v-if="col.field === 'actions'">
                          <Select
                              :options="formatActions(rowData[col.field])"
                              @change="handleActionBase($event.value, rowData)"
                              :pt="{ dropdown: { class: 'd-none' } }"
                              class="rounded rounded-10 d-flex justify-content-center align-items-center"
                          >
                            <template #option="{ option }">
                              <i :class="option.icon" class="mr-2" />
                              {{ option.label }}
                            </template>
                            <template #value>
                              <i class="fas fa-ellipsis d-flex justify-content-center align-items-center" />
                            </template>
                          </Select>
                        </span>
                        <span v-else v-html="renderCell(rowData, col)"></span>
                    </template>

                    <!-- Per‐Column Filter UI -->
                    <template #filter="{ filterModel, filterCallback }">
                        <!-- Text Filter -->
                        <InputText
                            v-if="col.filters && col.filters.includes('text')"
                            v-model="filterModel.constraints[0].value"
                            @input="() => { filterModel.constraints[0].matchMode = FilterMatchMode.CONTAINS;
                                filterCallback();}"
                            :placeholder="col.header"
                        />

                        <Select
                            v-else-if="col.filters && col.filters.includes('select')"
                            :options="col.options"
                            optionLabel="name"
                            optionValue="value"
                            v-model="filterModel.value"
                            @change="() => { filterModel.matchMode = FilterMatchMode.CONTAINS;
                            filterCallback();}"
                            :placeholder="col.header"
                        />
                    </template>
                </Column>
                <template #paginatorstart>
                </template>

                <template #paginatorend>
                    <!--                    <button v-if="exportable" :class="'btn ' + this.pageButton + ' text-white'" @click="exportCSV($event)">-->
                    <!--                        <i class="fa-regular fa-file-export" /> Export-->
                    <!--                    </button>-->
                </template>
            </DataTable>

            <!-- Refresh Button -->
            <div class="col-xs-12 col-sm-12 col-md-12 col-lg-12 text-right" v-if="isDev">
                <Button
                    icon="pi pi-refresh"
                    @click="fetchWhitelists"
                    class="p-button-rounded p-button-secondary"
                />
            </div>
        </Panel>
    </div>
</template>

<script>
import axios from 'axios';
import Toast from 'primevue/toast';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';
import {FilterMatchMode, FilterOperator } from '@primevue/core/api';

import { renderCell, formatActions } from '../../../composables/datatableCellRenderers.js';
import { handleActionBase, performAction } from '../../../composables/useDatatableActions.js';
import { initializeFilters} from "../../../composables/useDatatableFiltering";
import {ref} from "vue";

export default {
    name: 'WhitelistEntries',
    components: {
        Toast,
        DataTable,
        Column,
        InputText,
        Select,
    },
    props: {
        whitelistId: {
            type: String,
            required: true,
        },
    },
    emits: ['fetch-error'],
    data() {
        return {
            data: [],
            columns: [],
            filters: {
                global: {
                    value: null,
                    matchMode: FilterMatchMode.CONTAINS,
                },
            },
            filtersInitialized: false,
            totalRecords: 0,
            loading: true,
        };
    },

    computed: {
        FilterMatchMode: () => FilterMatchMode,

        pageButton() {
            return this.$themeSettings.pageButton;
        },
        pageInfo() {
            return this.$themeSettings.pageInfo;
        },
        pageBox() {
            return this.$themeSettings.pageBox;
        },
        filteredRecords() {
            return this.data.length;
        },
        isDev() {
            return process.env.NODE_ENV === 'development';
        },
    },

    mounted() {
        this.fetchWhitelists();
    },

    methods: {
        renderCell,
        formatActions,
        handleActionBase,
        initializeFilters,
        fetchWhitelists() {
            this.loading = true;
            axios.get(`/network-access/${this.whitelistId}/entries/datatable`, {
                headers: { Accept: 'application/json' },
            })
                .then(response => {
                    const payload = response.data;
                    this.data = payload.data;
                    this.columns = payload.columns;
                    this.totalRecords = payload.totalRecords;
                    if(!this.filtersInitialized) {
                        this.filters = this.initializeFilters(this.columns);
                        this.filtersInitialized = true;
                    }
                })
                .catch(err => {
                    this.$emit('fetch-error', {
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to load whitelists',
                        life: 3000,
                    });
                })
                .finally(() => {
                    this.loading = false;
                });
        },
    },
};
</script>

<style scoped>
.p-datatable-thead > tr > th {
    border-width: 1px 1px 2px 1px;
}

.p-datatable-header-cell {
    border-width: 1px 1px 2px 1px !important;
}

.p-datatable-mask {
    background-color: rgba(0, 0, 0, 0.4) !important;
}

.p-inputtext {
    font-size: 0.8rem;
}
</style>
